/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { EMPTY, Observable } from 'rxjs';
import { filter, takeUntil, map } from 'rxjs/operators';
import { alternativeBufferTime } from 'src/app/plain-javascript/services/rxjs-internal-extensions';

import { TicketList } from 'src/app/common/fancy-firebase-ticket/lists/TicketList';
import {
	TicketActionTypes,
	FancyTicketAction,
	TicketDuplicatedAction,
} from 'src/app/app-data/actions/ticket-actions';
import { PlanState } from 'src/app/services/plan-state.service';

import { UndoItem } from './UndoItem';
import { UndoTypes, UndoTitles } from './UndoTypes';
import { BaseUndoOperation } from './base-undo-operation';
import { metaizeTicket, monitorExternalDeletion } from './undo-utils';
import { TicketMeta } from 'src/app/app-data/models/TicketMeta';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';

interface IDuplicateInvalidationCache {
	ticketIds?: { [key: string]: string };
}

interface IDuplicateUndoItem extends UndoItem {
	data: TicketMeta[];
	invalidationCache: IDuplicateInvalidationCache;
}

export class TicketDuplicateOperation extends BaseUndoOperation<
	IDuplicateInvalidationCache,
	IDuplicateUndoItem
> {
	public readonly undoType = UndoTypes.ticketDuplicate;

	public title = UndoTitles.ticketDuplicate;

	constructor(
		private ticketList: TicketList,
		private planState: PlanState,
		private previousDeletion$: Observable<FancyTicketAction[]>,
		private featureFlagManager: FeatureFlagManager
	) {
		super();

		ticketList.rawEvent$
			.pipe(
				filter((event) => event.type === TicketActionTypes.ticketDuplicated),
				map((event) => this.decorateTicketData(event)),
				alternativeBufferTime(100),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((payload) => {
				this.onPush(this.makeUndoItem(payload));
			});

		//emits whenever a child_removed event emits and no recent local deletion action _also_ fired same ticket id
		// assumes that multiple local deletions work the same as a single local deletion
		ticketList.rawEvent$
			.pipe(monitorExternalDeletion(this.previousDeletion$), takeUntil(this.shutdownSubject$))
			.subscribe((action) => {
				this.checkDeleteInvalidation(action.key);
			});
	}

	private decorateTicketData(event: FancyTicketAction) {
		const customFieldTicketValue = this.planState.customFieldsApi.cloneCustomFieldTicketValues(
			(<TicketDuplicatedAction>event).config.oldTicketId
		);

		return metaizeTicket(event.payload, null, customFieldTicketValue);
	}

	private makeUndoItem(data: TicketMeta[]): IDuplicateUndoItem {
		return {
			title: this.title,
			data,
			undoType: this.undoType,
			invalidationCache: this.makeInvalidationCache(data),
		};
	}

	private makeInvalidationCache(data: TicketMeta[]): IDuplicateInvalidationCache {
		const ids = {};
		data.forEach((meta) => (ids[meta.ticket.$id] = meta.ticket.$id));
		return {
			ticketIds: ids,
		};
	}

	private checkDeleteInvalidation(ticketId: string) {
		this.onVerifyInvalidation((cache: IDuplicateInvalidationCache, list: IDuplicateUndoItem[]) => {
			if (!cache.ticketIds[ticketId]) {
				return null;
			}

			return list.filter((item) => item.invalidationCache.ticketIds[ticketId]);
		});
	}

	public applyUndo(undoItem: IDuplicateUndoItem) {
		const cacheData = undoItem.data;
		if (cacheData && cacheData.length) {
			this.planState.actions.internalDeleteTicket(cacheData.map((meta) => meta.ticket));
		}
		// note: custom field deletion is already handled in by ticketEventHandling() in plan-state.service triggered by internalDeleteTicket()
		return EMPTY;
	}

	public applyRedo(undoItem: IDuplicateUndoItem) {
		const cacheData = undoItem.data;
		if (cacheData && cacheData.length) {
			cacheData.forEach((meta) => {
				this.planState.actions.createTicketWithId(meta.ticket.$id, meta.ticket.prepareDataForSave());
				this.planState.customFieldsApi.createCustomFieldTicketValues(
					meta.ticket.$id,
					meta.customFieldTicketValues
				);
			});
		}
		return EMPTY;
	}

	public calculateCombinedInvalidation(
		list: IDuplicateUndoItem[],
		existingCache: IDuplicateInvalidationCache
	) {
		const ticketIds = {};

		list.forEach((item) => {
			for (const id in item.invalidationCache.ticketIds) {
				if (id) {
					ticketIds[id] = id;
				}
			}
		});

		return { ticketIds };
	}
}
